import { useState } from 'react';
import Image from 'next/image';
import { Typography } from '@eventbrite/marmalade';
import { IconButton } from '@eventbrite/legacy-eds.icon-button';
import { ShareIosChunky } from '@eventbrite/legacy-eds.iconography';
import { useIntl } from 'react-intl';
import type { Collection } from '@/types/collection';
import styles from './CollectionCard.module.scss';
import { FallbackImage } from './FallbackImage/FallbackImage';
import { CollectionShareModal } from './CollectionShareModal';

export type CollectionCardEventCount =
  { status: 'loading' } | { status: 'error' } | { status: 'success'; count: number };

interface Props {
  collection: Collection;
  eventCount: CollectionCardEventCount;
}

export default function CollectionCard(props: Props) {
  const intl = useIntl();
  const { title, summary, imageUrl, url } = props.collection;
  const { eventCount } = props;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const hasImage = imageUrl.trim().length > 0;

  return (
    <article className={styles.card}>
      <div className={styles.imageWrapper}>
        {hasImage ? (
          <Image
            src={imageUrl}
            alt=""
            fill
            className={styles.image}
            sizes="(max-width: 640px) 100vw, 33vw"
          />
        ) : (
          <FallbackImage />
        )}
        <div className={styles.shareSlot}>
          <IconButton
            style="outline"
            iconType={<ShareIosChunky />}
            onClick={() => setIsShareModalOpen(true)}
            title={intl.formatMessage(
              { id: 'Share {title}', defaultMessage: 'Share {title}' },
              { title },
            )}
          />
        </div>
      </div>
      <div className={styles.body}>
        <Typography variant="body-md-bold" as="h3" className={styles.title}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.titleLink}
            aria-label={intl.formatMessage(
              {
                id: '{title} (opens in a new tab)',
                defaultMessage: '{title} (opens in a new tab)',
              },
              { title },
            )}
          >
            {title}
          </a>
        </Typography>
        {eventCount.status === 'loading' ? (
          <div
            className={styles.eventCountSkeleton}
            aria-busy="true"
            aria-label={intl.formatMessage({
              id: 'Loading event count',
              defaultMessage: 'Loading event count',
            })}
          />
        ) : (
          <Typography
            variant="body-sm"
            className={
              eventCount.status === 'error' ? styles.eventCountUnavailable : styles.eventCount
            }
            color={eventCount.status === 'error' ? 'neutral-600' : undefined}
          >
            {eventCount.status === 'error'
              ? '—'
              : eventCount.count === 0
                ? intl.formatMessage({
                    id: 'No upcoming events',
                    defaultMessage: 'No upcoming events',
                  })
                : intl.formatMessage(
                    {
                      id: '{upcomingEventCount, plural, one {# upcoming event} other {# upcoming events}}',
                      defaultMessage:
                        '{upcomingEventCount, plural, one {# upcoming event} other {# upcoming events}}',
                    },
                    { upcomingEventCount: eventCount.count },
                  )}
          </Typography>
        )}
        <Typography variant="body-sm" color="neutral-600" className={styles.summary}>
          {summary}
        </Typography>
      </div>

      <CollectionShareModal
        isShown={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        collection={props.collection}
      />
    </article>
  );
}
